import React, { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Mail, Megaphone } from "lucide-react";
import { NAV_ORDER, TYPES } from "@/config";

export default function About() {
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      const el = document.querySelector(location.hash);
      if (el) el.scrollIntoView({ behavior: "smooth" });
    }
  }, [location]);

  return (
    <div>
      <header className="border-b border-line bg-surface">
        <div className="mx-auto max-w-[900px] px-5 lg:px-10 py-24">
          <div className="eyebrow mb-4 text-gold">About the Project</div>
          <h1 className="font-serif text-5xl leading-tight tracking-tight text-parchment lg:text-6xl">
            An archive of art crime, law, and controversy.
          </h1>
        </div>
      </header>

      <div className="mx-auto max-w-[760px] px-5 lg:px-10 py-16 prose-editorial">
        <p>
          COA — Crimes of Art — is being built as the definitive online library and publication
          dedicated to art crime, law, and controversy. It documents theft, forgery, fraud, looting,
          restitution, cultural heritage crime, censorship, authentication disputes, museum scandals,
          and the legal battles and provocations that shape how we make, hold, and lose the objects
          we call priceless.
        </p>
        <p>
          Not everyone in these pages is a criminal. Alongside thieves and forgers stand the artists
          who tested boundaries, the investigators who chased stolen works, the institutions caught
          in scandal, and the courts that decided who owns the past. COA is structured like an
          institution rather than a true-crime feed: every case connects to the people, artworks,
          and museums around it, so one entry opens outward into a network of evidence, biography,
          and consequence designed to grow into thousands of interlinked records.
        </p>
        <p>
          Alongside the library sits a working publication: reported news, long-form essays, a
          podcast, and a lighter gallery of museum mishaps. And at any point you may consult the
          Archivist, an assistant grounded in the collection, to ask questions and request summaries.
        </p>
      </div>

      <div className="mx-auto max-w-[900px] px-5 lg:px-10 pb-24">
        <div className="eyebrow mb-6 border-b border-gold/30 pb-3">Explore the Collection</div>
        <div className="grid gap-4 sm:grid-cols-2">
          {NAV_ORDER.map((k) => (
            <Link key={k} to={`/${TYPES[k].segment}`}
              className="group border border-line p-6 hover:border-gold/60 transition-colors">
              <div className="font-serif text-2xl text-parchment group-hover:text-gold transition-colors">{TYPES[k].label}</div>
              <p className="mt-2 text-sm text-faint">{TYPES[k].blurb}</p>
            </Link>
          ))}
        </div>
      </div>

      <section id="contact" data-testid="contact-section" className="border-t border-line bg-surface scroll-mt-24">
        <div className="mx-auto max-w-[900px] px-5 lg:px-10 py-20">
          <div className="eyebrow mb-4 text-gold">Contact</div>
          <h2 className="font-serif text-4xl leading-tight text-parchment lg:text-5xl">
            Get in touch with COA.
          </h2>
          <p className="mt-5 max-w-2xl text-base leading-relaxed text-faint">
            Tips, corrections, contributions, press, and rights enquiries are all welcome.
            Reach the editorial desk directly by email.
          </p>
          <a
            href="mailto:crimesofart@gmail.com"
            data-testid="contact-email-link"
            className="mt-8 inline-flex items-center gap-3 border border-gold/50 px-6 py-3.5 font-serif text-xl text-parchment transition-colors hover:border-gold hover:text-gold"
          >
            <Mail size={18} className="text-gold" />
            crimesofart@gmail.com
          </a>

          <div data-testid="call-for-information" id="submit-tip" className="mt-14 scroll-mt-28 border border-gold/40 bg-elevated/40 p-8 lg:p-10">
            <div className="eyebrow mb-4 flex items-center gap-2 text-gold">
              <Megaphone size={14} /> Public Call for Information
            </div>
            <h3 className="font-serif text-3xl italic leading-tight text-parchment lg:text-4xl">
              Know a dirty dealer? Chasing a stolen masterpiece?
            </h3>
            <p className="mt-5 max-w-2xl text-base leading-relaxed text-faint">
              Send us your tips on art theft, forgery, looted antiquities, shady dealers, museum
              scandals, and unsolved mysteries. If it's a crime involving art, COA wants to hear
              about it.
            </p>
            <a
              href="mailto:crimesofart@gmail.com?subject=COA%20Tip%20%E2%80%94%20Information"
              data-testid="submit-tip-link"
              className="mt-8 inline-flex items-center gap-3 bg-gold px-6 py-3.5 text-xs font-semibold uppercase tracking-widest text-ink transition-colors hover:bg-gold-hover"
            >
              <Mail size={16} /> Submit a Tip
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
